import React from "react";
import { motion } from "framer-motion";
import { TID } from "@/lib/testIds";
import { Apple, Smartphone } from "lucide-react";
import { useT } from "@/lib/i18n";
import Wordmark from "@/components/sentinel/Wordmark";

const LOGO_URL =
    "https://customer-assets-jt897jd0.emergentagent.net/job_417ef979-c5a1-480b-a52d-ba174268846c/artifacts/hbgmknso_669BBB83-039C-4BBD-AA28-5D92E2D6BCAC.png";

export default function Footer() {
    const { t } = useT();
    return (
        <footer
            data-testid={TID.footer.root}
            className="bg-sn-black text-sn-silver pt-24 pb-10 border-t border-white/5"
        >
            <div className="max-w-[1400px] mx-auto px-6 md:px-10">
                <motion.div
                    initial={{ opacity: 0, y: 40 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, amount: 0.4 }}
                    transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
                    className="pb-20 border-b border-white/10"
                >
                    <div className="font-brand text-[22vw] md:text-[16vw] leading-[0.85] tracking-[0.02em] uppercase">
                        <span className="text-sn-silver">SENTI</span>
                        <span className="text-sn-blue">NEL</span>
                    </div>
                </motion.div>

                <div className="grid grid-cols-1 md:grid-cols-12 gap-10 py-16">
                    <div className="md:col-span-5">
                        <div className="flex items-center gap-3">
                            <img
                                src={LOGO_URL}
                                alt="Sentinel"
                                className="w-10 h-10 object-contain"
                            />
                            <div>
                                <Wordmark size="text-xl" />
                                <div className="font-mono-e text-[10px] text-sn-fog uppercase tracking-widest mt-1">
                                    {t("footer.tagline")}
                                </div>
                            </div>
                        </div>
                        <p className="mt-6 max-w-md text-sn-fog leading-relaxed">
                            {t("footer.about")}
                        </p>
                    </div>

                    <div className="md:col-span-3">
                        <div className="font-mono-e text-[10px] tracking-[0.28em] text-sn-fog uppercase mb-5">
                            {t("footer.navigate")}
                        </div>
                        <ul className="space-y-3 font-serif text-lg">
                            <li>
                                <a
                                    href="#manifesto"
                                    className="text-sn-silver hover:text-sn-blue transition-colors duration-300"
                                >
                                    {t("nav.manifesto")}
                                </a>
                            </li>
                            <li>
                                <a
                                    href="#rete"
                                    className="text-sn-silver hover:text-sn-blue transition-colors duration-300"
                                >
                                    {t("nav.rete")}
                                </a>
                            </li>
                            <li>
                                <a
                                    href="#come-funziona"
                                    className="text-sn-silver hover:text-sn-blue transition-colors duration-300"
                                >
                                    {t("nav.comeFunziona")}
                                </a>
                            </li>
                            <li>
                                <a
                                    href="#codici"
                                    className="text-sn-silver hover:text-sn-blue transition-colors duration-300"
                                >
                                    {t("nav.codici")}
                                </a>
                            </li>
                            <li>
                                <a
                                    href="#faq"
                                    className="text-sn-silver hover:text-sn-blue transition-colors duration-300"
                                >
                                    {t("nav.faq")}
                                </a>
                            </li>
                        </ul>
                    </div>

                    <div className="md:col-span-4">
                        <div className="font-mono-e text-[10px] tracking-[0.28em] text-sn-fog uppercase mb-5">
                            {t("footer.coming")}
                        </div>
                        <div className="space-y-3">
                            <div className="flex items-center gap-3 px-5 py-3 rounded-full border border-white/10 text-sn-silver">
                                <Apple size={16} className="text-sn-blue" />
                                <div>
                                    <div className="font-mono-e text-[9px] tracking-widest text-sn-fog uppercase">
                                        {t("footer.comingSoon")}
                                    </div>
                                    <div className="text-sm">App Store</div>
                                </div>
                            </div>
                            <div className="flex items-center gap-3 px-5 py-3 rounded-full border border-white/10 text-sn-silver">
                                <Smartphone size={16} className="text-sn-blue" />
                                <div>
                                    <div className="font-mono-e text-[9px] tracking-widest text-sn-fog uppercase">
                                        {t("footer.comingSoon")}
                                    </div>
                                    <div className="text-sm">Google Play</div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="hairline-dark" />

                <div className="pt-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-4 font-mono-e text-[10px] uppercase tracking-widest text-sn-fog">
                    <div className="flex flex-col md:flex-row md:items-center gap-2 md:gap-4">
                        <span>{t("footer.rights")}</span>
                        <span className="hidden md:inline">·</span>
                        <a
                            href="https://sentinelsocial.app"
                            className="text-sn-silver hover:text-sn-blue transition-colors duration-300"
                        >
                            sentinelsocial.app
                        </a>
                    </div>
                    <div className="flex items-center gap-6">
                        <span>{t("footer.made")}</span>
                        <a
                            href="#top"
                            className="text-sn-silver hover:text-sn-blue transition-colors duration-300"
                        >
                            {t("footer.top")}
                        </a>
                    </div>
                </div>
            </div>
        </footer>
    );
}
